import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { HlmBtnDirective, HlmIconComponent } from '../../../ui';
import { BrandReadonlyBannerComponent } from '../brand-readonly-banner.component';
import { PermissionService } from '../../../core/auth/permission.service';
import { BrandService } from '../brand.service';
import { Brand } from '../brand.model';
import { BrandLogoComponent } from '../brand-logo.component';
import { BrandNumberBlocksPanelComponent } from './brand-number-blocks-panel.component';

/**
 * Standalone page wrapper for the per-brand Number Blocks management UI
 * (`/admin/brands/:brandId/numberblocks`). Renders the breadcrumb + hero and
 * delegates all grid/selection/attach/detach logic to the shared
 * {@link BrandNumberBlocksPanelComponent}, which is also embedded inline in the
 * brand detail/edit page.
 */
@Component({
  selector: 'cmt-brand-number-blocks-page',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterLink,
    HlmBtnDirective,
    HlmIconComponent,
    BrandLogoComponent,
    BrandReadonlyBannerComponent,
    BrandNumberBlocksPanelComponent,
  ],
  templateUrl: './brand-number-blocks-page.component.html',
})
export class BrandNumberBlocksPageComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly brandService = inject(BrandService);
  private readonly permissionService = inject(PermissionService);
  private readonly destroyRef = inject(DestroyRef);

  /** EB-3709: disable mutating actions for read-only users (no amie.brandManager.write). */
  protected readonly canWrite = computed(() => this.permissionService.canWrite('brandManager'));

  protected readonly brandId = signal<number | null>(null);
  protected readonly brand = signal<Brand | null>(null);

  ngOnInit(): void {
    const id = Number(this.route.snapshot.paramMap.get('brandId'));
    if (!id || Number.isNaN(id)) {
      this.router.navigate(['/admin/brands']);
      return;
    }
    this.brandId.set(id);
    this.loadBrand(id);
  }

  private loadBrand(id: number): void {
    this.brandService.getBrand(id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: b => this.brand.set(b),
      error: () => {
        // Soft-fail: page still shows the panel with a numeric title.
      },
    });
  }
}
